import { useState, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { api, useTenants, type Tenant } from '../lib/api';
import { useRole } from '../lib/role';
import { dotClass, statusLabel } from '../lib/status';

const STATUSES = ['running','queued','rerun_queued','done','warning','failed','paused'] as const;

export default function TenantList() {
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const clientSlug = params.get('client') || undefined;
  const { data, isLoading } = useTenants(clientSlug ? { client_slug: clientSlug } : undefined);
  const { isAdmin } = useRole();

  const [statusFilter, setStatusFilter] = useState<string>('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [exporting, setExporting] = useState(false);

  const tenants = useMemo(() => {
    let rows = data || [];
    if (statusFilter) rows = rows.filter(t => t.status === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter(t =>
        t.target_domain.toLowerCase().includes(q)
        || t.ga_upn.toLowerCase().includes(q)
        || (t.display_name || '').toLowerCase().includes(q),
      );
    }
    return rows;
  }, [data, statusFilter, search]);

  const toggleOne = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const visibleIds = tenants.map(t => t.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selected.has(id));
  const toggleAllVisible = () => {
    setSelected(prev => {
      const next = new Set(prev);
      if (allVisibleSelected) visibleIds.forEach(id => next.delete(id));
      else visibleIds.forEach(id => next.add(id));
      return next;
    });
  };
  const downloadCsv = async () => {
    if (selected.size === 0 || exporting) return;
    setExporting(true);
    try {
      const blob = await api.exportMailboxesCsv(Array.from(selected));
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mailboxes-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      alert(`Export failed: ${e?.message || e}`);
    } finally {
      setExporting(false);
    }
  };
  const downloadAdminCsv = async () => {
    if (selected.size === 0 || exporting) return;
    setExporting(true);
    try {
      const blob = await api.exportTenantAdminCredentialsCsv(Array.from(selected));
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tenant-admin-credentials-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      alert(`Admin export failed: ${e?.message || e}`);
    } finally {
      setExporting(false);
    }
  };

  // Counts per status (over the un-status-filtered set so chips reflect availability).
  const statusCounts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const t of (data || [])) c[t.status] = (c[t.status] || 0) + 1;
    return c;
  }, [data]);

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <header className="flex items-end justify-between mb-6 gap-4">
        <div>
          <h1 className="page-h1 text-xl">Tenants</h1>
          <p className="page-sub mt-1">
            {isAdmin && clientSlug && (
              <>Filtered to <span className="text-ink font-mono">{clientSlug}</span>{' · '}</>
            )}
            <span className="tabular-nums">{(data || []).length}</span> total
          </p>
        </div>
        <div className="flex items-center gap-2">
          {selected.size > 0 && (
            <>
              <button
                onClick={downloadCsv}
                disabled={exporting}
                className="btn-secondary btn-lg disabled:opacity-50"
                title="Every mailbox in the selected tenants (full_name, email, password)"
              >
                {exporting ? 'Exporting…' : `Mailboxes CSV (${selected.size})`}
              </button>
              <button
                onClick={downloadAdminCsv}
                disabled={exporting}
                className="btn-secondary btn-lg disabled:opacity-50"
                title="M365 admin credentials per tenant (target_domain, ga_upn, ga_password)"
              >
                {exporting ? 'Exporting…' : `Admin CSV (${selected.size})`}
              </button>
            </>
          )}
          <Link to="/new" className="btn-primary btn-lg">+ New tenant</Link>
        </div>
      </header>

      {/* Filters */}
      <div className="flex gap-2 mb-4 flex-wrap items-center">
        <input
          className="input max-w-xs"
          placeholder="Filter by domain, tenant login, or display name…"
          value={search} onChange={e => setSearch(e.target.value)}
        />
        <div className="flex gap-1.5 flex-wrap">
          <StatusChip
            label="all" active={statusFilter === ''} count={(data || []).length}
            onClick={() => setStatusFilter('')}
          />
          {STATUSES.map(s => statusCounts[s] ? (
            <StatusChip
              key={s} label={s} active={statusFilter === s} count={statusCounts[s] || 0}
              onClick={() => setStatusFilter(statusFilter === s ? '' : s)}
            />
          ) : null)}
        </div>
      </div>

      {/* Body */}
      {isLoading ? (
        <div className="card p-12 text-center text-dim">Loading…</div>
      ) : tenants.length === 0 ? (
        <div className="card empty">
          {(data || []).length === 0
            ? <>No tenants yet. <Link to="/new" className="text-accent hover:underline ml-1">Start one</Link></>
            : <>No tenants match these filters.</>}
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 32 }} onClick={e => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    aria-label="Select all visible tenants"
                    checked={allVisibleSelected}
                    onChange={toggleAllVisible}
                  />
                </th>
                <th style={{ width: 28 }}></th>
                <th>Domain</th>
                {isAdmin && <th>Client</th>}
                <th>Tenant login</th>
                {isAdmin && <th>Phase</th>}
                <th>Connected</th>
                <th className="text-right">Status</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map(t => (
                <TenantRow
                  key={t.id} t={t} isAdmin={isAdmin}
                  checked={selected.has(t.id)}
                  onToggle={() => toggleOne(t.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function TenantRow({
  t, isAdmin, checked, onToggle,
}: {
  t: Tenant; isAdmin: boolean; checked: boolean; onToggle: () => void;
}) {
  const nav = useNavigate();
  return (
    <tr className="cursor-pointer" onClick={() => nav(`/tenants/${t.id}`)}>
      <td onClick={e => e.stopPropagation()}>
        <input
          type="checkbox"
          aria-label={`Select ${t.target_domain}`}
          checked={checked}
          onChange={onToggle}
        />
      </td>
      <td>
        <span className={`${dotClass(t.status)}`} />
      </td>
      <td className="font-medium">
        <Link to={`/tenants/${t.id}`} className="hover:text-accent transition">
          {t.target_domain}
        </Link>
      </td>
      {isAdmin && (
        <td>
          {t.client_slug
            ? <span className="pill-dim">{t.client_slug}</span>
            : <span className="text-dim text-xs">—</span>}
        </td>
      )}
      <td className="font-mono text-xs text-dim">{t.ga_upn}</td>
      {isAdmin && (
        <td className="text-xs">
          {t.current_phase
            ? <span className="text-ink">{t.current_phase}</span>
            : <span className="text-dim">—</span>}
        </td>
      )}
      <td>
        <ConnectedCell t={t} />
      </td>
      <td className="text-right">
        <StatusPill status={t.status} />
      </td>
    </tr>
  );
}


function ConnectedCell({ t }: { t: Tenant }) {
  // Use mailbox_total (what's actually been created) as the denominator
  // — early in the pipeline, mailbox rows don't exist yet, so showing
  // "0 / 100" would mislead an operator into thinking the run is stuck.
  // Fall back to the configured mailbox_count so they always see the
  // target. Bar fill is over whichever number is larger so a partial
  // create-mailboxes phase doesn't render past 100%.
  const connected = t.connected_count || 0;
  const errors = t.error_count || 0;
  const total = t.mailbox_total || t.mailbox_count || 0;
  if (total <= 0) {
    return <span className="text-dim text-xs tabular-nums">—</span>;
  }
  const pct = Math.max(0, Math.min(100, Math.round((connected / total) * 100)));
  // Green at >=90% connected (matches the server's _OAUTH_SUCCESS_THRESHOLD
  // — operator only cares that most of what they ordered is usable).
  // Warn when the pipeline has settled (errors + connected >= total) but
  // we're still below the threshold; otherwise still ticking up.
  const fill = connected / total >= 0.9 ? 'bg-ok'
    : errors > 0 && connected + errors >= total ? 'bg-warn'
    : 'bg-accent';
  return (
    <div className="flex items-center gap-2 w-[160px]">
      <div className="flex-1 h-1.5 rounded-full overflow-hidden bg-white/[0.04]">
        <div className={`h-full ${fill} transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[11px] tabular-nums text-dim shrink-0">
        {connected}<span className="opacity-60">/{total}</span>
        {errors > 0 && <span className="text-warn ml-1">·{errors}</span>}
      </span>
    </div>
  );
}


function StatusChip({
  label, active, count, onClick,
}: { label: string; active: boolean; count: number; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`text-[11px] px-2.5 py-1 rounded-md border transition
        ${active
          ? 'bg-accent/15 border-accent/40 text-ink'
          : 'border-line text-dim hover:text-ink hover:border-line2'}`}
    >
      <span className="capitalize">{label.replace('_', ' ')}</span>
      <span className="ml-1.5 opacity-70 tabular-nums">{count}</span>
    </button>
  );
}


function StatusPill({ status }: { status: Tenant['status'] }) {
  const cls =
    status === 'done'    ? 'pill-ok'
  : status === 'failed'  ? 'pill-err'
  : status === 'warning' ? 'pill-warn'
  : status === 'running' ? 'pill-run'
  : 'pill-dim';
  return <span className={cls}>{statusLabel(status)}</span>;
}
